// Print-AREA-SIZE options (cm2) for methods whose real selectable tiers are
// physical print size, not color count -- e.g. PFConcept's DTF Transfer,
// Lasergraveren, Sublimatie, Digitale print, Borduren/3D borduren. A SEPARATE
// dimension from printColors.ts's PrintColorOption: a method has EITHER
// color-count tiers OR area-size tiers in practice (a vendor feed marks an
// area-size-tiered method's own maxColours as "full color" -- there's nothing
// to pick by color), never both for the same side. See printColorOptions'
// own doc comment in schema.prisma for the parallel color-count model this
// mirrors.
export type PrintAreaSizeOption = number;

// Admin-facing serialize/parse for the comma-separated `printAreaSizeOptions`
// DB column (ProductConfig.printAreaSizeOptions / ImageSideConfig's per-side
// override) -- same convention as printColorOptions' serializePrintColorOptions.
export function serializePrintAreaSizeOptions(options: PrintAreaSizeOption[]): string {
  return options.map(String).join(',');
}

export function parsePrintAreaSizeOptions(raw: string | null | undefined): PrintAreaSizeOption[] {
  if (!raw) return [];
  return raw
    .split(',')
    .map((s) => Number(s.trim()))
    .filter((n): n is number => Number.isFinite(n) && n > 0);
}

// Builds an ImageSideConfig.printAreaSizeOptions string from a vendor's raw
// comma-separated area-size-tier field (PFConcept's logoSizeTiersCm2, e.g.
// "100, 300" or "12, 36, 60, 180"). Mirrors printColorOptionsFromVendorCounts'
// own vendor-count-parsing convention: non-numeric entries are dropped rather
// than throwing (unattended over large vendor feeds), and an entirely empty/
// unusable input returns null (not an empty string) so callers can
// distinguish "no vendor tier data" from "leave unset".
export function printAreaSizeOptionsFromVendorTiers(
  raw: string | null | undefined,
): string | null {
  if (!raw) return null;
  const tiers = raw
    .split(',')
    .map((s) => Number(s.trim()))
    .filter((n): n is number => Number.isFinite(n) && n > 0);
  if (!tiers.length) return null;
  return serializePrintAreaSizeOptions(tiers);
}

// PFConcept-only: reproduces the exact number Magento's live PDP shows for a
// full-color, size-tiered print area, so the CustomEdge designer offers the
// SAME figure the customer already saw before clicking "Design It" -- even
// where that figure doesn't match this area's own vendor logoSizeTiersCm2.
// Root-caused 2026-08-12 by reading the actual PIM import job
// (Nits/XmlConnectorBundle/Reader/File/XmlProductReader.php, the code that
// writes Magento's real custom-option values -- PFConcept only, MidOcean's
// own equivalent job resolves options per PARENT product and was confirmed
// correct against its own vendor feed, no equivalent gap found there):
//
// 1. PIM computes its own "$maxPrintSize" from a DIFFERENT field than
//    logoSizeTiersCm2 -- it re-derives width*height/100 (mm -> cm2) from the
//    print area's own physical dimensions, i.e. exactly this codebase's own
//    `maxAreaCm2` (see PFCONCEPT.md: maxAreaCm2 is already `sizeMm.width *
//    sizeMm.height / 100`, confirmed identical by direct computation).
// 2. Only the LARGEST vendor tier is capped down to maxAreaCm2, and only
//    when that tier exceeds maxAreaCm2 -- every smaller tier is left
//    untouched (verified against 7 live SKUs, 2026-08-12: PX120737's vendor
//    tiers 100/300/500 show on the real PDP as 100/300/480, where 480 is
//    that SKU's own maxAreaCm2 -- not a generic "always show maxAreaCm2"
//    rule, and not "drop the largest tier", specifically a cap).
// 3. When a print area has NO logoSizeTiersCm2 at all (PFConcept's
//    "Digitale transfer" areas confirmed to genuinely never carry tier data
//    in the raw feed), PIM falls through to a STALE, SHARED translation-
//    table default ("Digitale transfer 0" -> "Full color max 50 cm2") that
//    is the same literal "50" across every affected product regardless of
//    that product's own maxAreaCm2 (confirmed live: PX120741 maxAreaCm2=165,
//    PX120742 maxAreaCm2=132, PX120722=70, PX120724=200, PX120726=144,
//    PX120743=20 -- six different real areas, one identical PDP number).
//    Reproduced here as a literal constant, NOT derived from maxAreaCm2,
//    because that's genuinely what's live -- this is a known-stale PIM
//    value being mirrored for storefront consistency, not a "better"
//    number. If PIM's translation-master row is ever corrected, this
//    constant goes stale and needs updating to match (there is no way to
//    derive the current live value from vendor data alone in this case).
const PFCONCEPT_NO_TIER_DEFAULT_CM2 = 50;

// `maxColours` gates the whole function the same way it gates PIM's own
// option_level logic (see HandleOptionsJob.php/XmlProductReader.php -- the
// "no tiers -> stale 50cm2 fallback" branch is only ever reachable for a
// print area whose maxColours is the literal "full color"; a spot-color
// method like PFConcept's plain "Transfer" (maxColours="1") also commonly
// has an empty logoSizeTiersCm2, but PIM never runs it through this size-tier
// logic at all -- it stays a color-count option. Confirmed live 2026-08-12:
// PX104157's "Transfer" side (maxColours=1) incorrectly got tagged
// printAreaSizeOptions=50 before this gate was added, which would have shown
// a nonsensical "Select Size: 50cm2" step for a method the PDP never does.
export function pfconceptAreaSizeOptionsMatchingPdp(
  logoSizeTiersCm2: string | null | undefined,
  maxAreaCm2: string | number | null | undefined,
  maxColours: string | null | undefined,
): string | null {
  if (maxColours?.trim().toLowerCase() !== 'full color') return null;

  const maxArea = Number(maxAreaCm2);
  const hasMaxArea = Number.isFinite(maxArea) && maxArea > 0;

  const tiers = (logoSizeTiersCm2 ?? '')
    .split(',')
    .map((s) => Number(s.trim()))
    .filter((n): n is number => Number.isFinite(n) && n > 0);

  if (!tiers.length) {
    // No vendor tier data at all -- PIM's stale shared fallback.
    return serializePrintAreaSizeOptions([PFCONCEPT_NO_TIER_DEFAULT_CM2]);
  }

  if (!hasMaxArea) return serializePrintAreaSizeOptions(tiers);

  const sorted = [...tiers].sort((a, b) => a - b);
  const largest = sorted[sorted.length - 1];
  if (largest > maxArea) sorted[sorted.length - 1] = maxArea;
  return serializePrintAreaSizeOptions(sorted);
}
